import { useMemo } from 'react';
import { useAppSelector } from '../../../../store/hooks';
import { PartnerLocaleType } from '../../../../common/models/Partner';
import { PartnerProfileConfig, PartnerProfileResourceSectionType, PartnerResourceTranslation } from '../../../../common/models/PartnerProfile';
import { selectPartnerProfileConfig } from '../../../../store/slices/dashboardSlice';

export interface PartnerIntroduction {
  title: string;
  description: string;
}

export interface MappedPartnerProfileConfig extends PartnerProfileConfig {
  highlights?: string[];
  introduction: PartnerIntroduction;
  profileCategory?: string[];
  partnerMediaResources: PartnerResourceTranslation[];
}

const EMPTY_CONFIG = {} as MappedPartnerProfileConfig;

export function usePartnerProfileConfig(): MappedPartnerProfileConfig {
  const partnerProfileConfig = useAppSelector(selectPartnerProfileConfig);

  return useMemo(() => {
    if (!partnerProfileConfig) {
      return EMPTY_CONFIG;
    }

    const {
      partnerOffers,
      partnerBenefits,
      partnerResources,
      config,
    } = partnerProfileConfig;

    const offersEngTranslation = partnerOffers?.filter(
      (translation: { locale: PartnerLocaleType; }) => translation.locale ===
        PartnerLocaleType.ENGLISH,
    );
    const benefitsEngTranslation = partnerBenefits?.filter(
      (translation: { locale: PartnerLocaleType; }) => translation.locale ===
        PartnerLocaleType.ENGLISH,
    );
    const resourcesEngTranslation = partnerResources?.filter(
      (translation: { locale: PartnerLocaleType; }) => translation.locale ===
        PartnerLocaleType.ENGLISH,
    );

    // Split resources by section type:
    // - related_resources → Related Articles section
    // - media → Resources section (PDF downloads, videos)
    const relatedArticles = resourcesEngTranslation?.filter(
      (r) => r.resourceSection === PartnerProfileResourceSectionType.RelatedResources ||
        r.resourceSection === PartnerProfileResourceSectionType.Articles,
    ) ?? [];
    const mediaResources = resourcesEngTranslation?.filter(
      (r) => r.resourceSection === PartnerProfileResourceSectionType.Resources ||
       r.resourceSection === PartnerProfileResourceSectionType.Media,
    ) ?? [];

    const partnerConfigEngTranslation = config?.partnerConfigTranslations.find(
      (translation: { locale: PartnerLocaleType; }) => translation.locale ===
        PartnerLocaleType.ENGLISH,
    );
    const intro: PartnerIntroduction = {
      title: partnerConfigEngTranslation?.title || '',
      description: partnerConfigEngTranslation?.profileDescription ||
        partnerConfigEngTranslation?.description || '',
    };

    return {
      ...partnerProfileConfig,
      partnerOffers: offersEngTranslation,
      partnerBenefits: benefitsEngTranslation,
      partnerResources: relatedArticles,
      partnerMediaResources: mediaResources,
      highlights: partnerConfigEngTranslation?.perks,
      introduction: intro,
      profileCategory: partnerConfigEngTranslation?.profileCategory,
    };
  }, [partnerProfileConfig]);
}
